/**
 * Authentication Service for Existing Patient New Treatment Flow.
 * 
 * Handles:
 * - Email existence check (detect returning patients)
 * - Login with password for existing patients
 * - Session management with HTTP-only cookies
 * 
 * Phase 3 - Existing Patient New Treatment Feature
 */
import {
  DEFAULT_API_V1_BASE,
  normalizeApiV1Base,
} from "../constants/apiEndpoints";
import { PATIENT_AUTH_STORAGE_KEY } from "../constants/patientAuthStorage";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

const EMAIL_LOOKUP_TIMEOUT_MS = 3000;
const EMAIL_LOOKUP_CACHE_TTL_MS = 30000;

class ExistingPatientAuthService {
  constructor() {
    this.emailLookupCache = new Map();
    this.emailLookupRequests = new Map();

    // Use client-specific API endpoint (same as questionnaire operations)
    // Falls back to admin API if client endpoint not available
    this.getBaseURL = () => {
      const clientApiEndpoint = localStorage.getItem('welliemd_client_api_endpoint');
      if (clientApiEndpoint) {
        return normalizeApiV1Base(clientApiEndpoint);
      }
      // Fallback to admin API
      return process.env.REACT_APP_API_URL || DEFAULT_API_V1_BASE;
    };
  }

  /**
   * Check if an email address has an existing account.
   * Used to detect returning patients and prompt them to sign in.
   * 
   * @param {string} email - Email address to check
   * @returns {Promise<{exists: boolean, email: string}>}
   */
  async checkEmailExists(email) {
    const normalizedEmail = typeof email === 'string'
      ? email.trim().toLowerCase()
      : '';
    if (!normalizedEmail) {
      return { exists: false, email: '' };
    }

    const cached = this.emailLookupCache.get(normalizedEmail);
    if (cached && cached.expiresAt > Date.now()) return { ...cached.result };
    this.emailLookupCache.delete(normalizedEmail);

    const inFlight = this.emailLookupRequests.get(normalizedEmail);
    if (inFlight) return inFlight;

    const request = (async () => {
      try {
        const baseURL = this.getBaseURL();
        const url = `${baseURL}/questionnaires/auth/check-email/`;
        const response = await fetchWithTimeout(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ email: normalizedEmail }),
        }, EMAIL_LOOKUP_TIMEOUT_MS);

        if (!response.ok) {
          return {
            exists: false,
            available: false,
            email: normalizedEmail,
          };
        }

        const data = await response.json();
        const result = {
          exists: data.exists === true,
          available: true,
          email: data.email || normalizedEmail,
        };
        this.emailLookupCache.set(normalizedEmail, {
          result,
          expiresAt: Date.now() + EMAIL_LOOKUP_CACHE_TTL_MS,
        });
        return result;
      } catch (error) {
        // Lookup is advisory. A timeout or service failure must not block the
        // authoritative onboarding request when the patient clicks Continue.
        return {
          exists: false,
          available: false,
          email: normalizedEmail,
          errorCode: error?.code || 'email_lookup_unavailable',
        };
      }
    })();

    this.emailLookupRequests.set(normalizedEmail, request);
    request.finally(() => {
      if (this.emailLookupRequests.get(normalizedEmail) === request) {
        this.emailLookupRequests.delete(normalizedEmail);
      }
    });
    return request;
  }

  async sendEmailVerificationCode(email) {
    const response = await fetch(
      `${this.getBaseURL()}/questionnaires/auth/email/send-code/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Unable to send verification email.');
    }
    return data;
  }

  async verifyEmailCode(challengeId, code) {
    const response = await fetch(
      `${this.getBaseURL()}/questionnaires/auth/email/verify-code/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challengeId, code }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Unable to verify email.');
    }
    return data;
  }

  async sendPhoneVerificationCode(phone) {
    const response = await fetch(
      `${this.getBaseURL()}/questionnaires/auth/phone/send-code/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to send verification SMS.');
    return data;
  }

  async verifyPhoneCode(challengeId, code) {
    const response = await fetch(
      `${this.getBaseURL()}/questionnaires/auth/phone/verify-code/`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challengeId, code }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Unable to verify phone.');
    return data;
  }

  /**
   * Login existing patient and create a NEW_TREATMENT session.
   * 
   * @param {string} email - Patient email
   * @param {string} password - Patient password
   * @param {string} templateId - Questionnaire template ID
   * @returns {Promise<Object>} Login result with session info and prefill data
   */
  async login(email, password, templateId) {
    if (!email || !password || !templateId) {
      throw new Error('Email, password, and template ID are required');
    }

    const baseURL = this.getBaseURL();
    const url = `${baseURL}/questionnaires/auth/login/`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Enable cookies for cross-origin (sets refresh_token cookie)
      body: JSON.stringify({
        email: email.trim(),
        password,
        template_id: templateId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle specific error types
      if (response.status === 401) {
        throw new AuthError('Invalid email or password', 'INVALID_CREDENTIALS');
      }
      
      if (response.status === 403 && data.error === 'duplicate_visit_blocked') {
        throw new DuplicateVisitError(
          data.message || 'You have a recent visit for this treatment',
          data.days_remaining,
          data.blocked_until,
          data.refill_interval_days
        );
      }

      throw new AuthError(data.error || data.message || 'Login failed', 'LOGIN_FAILED');
    }

    // Store session info
    this.storeSession(data);

    return {
      sessionId: data.session_id,
      accessToken: data.access_token,
      accessTokenExpiresIn: data.access_token_expires_in,
      patientAccessToken: data.patient_access_token,
      patientAccessTokenExpiresIn: data.patient_access_token_expires_in,
      prefillData: data.prefill_data,
      patient: data.patient,
      template: data.template,
    };
  }

  /**
   * Store session information for later use.
   * @param {Object} data - Login response data
   */
  storeSession(data) {
    this.clearSession();

    if (data.access_token) {
      sessionStorage.setItem(
        PATIENT_AUTH_STORAGE_KEY.questionnaireAccessToken,
        data.access_token,
      );
      sessionStorage.setItem(
        PATIENT_AUTH_STORAGE_KEY.sessionId,
        data.session_id,
      );
      sessionStorage.setItem(
        PATIENT_AUTH_STORAGE_KEY.questionnaireAccessExpiresAt,
        Date.now() + ((Number(data.access_token_expires_in) || 3600) * 1000)
      );
    }

    if (data.patient_access_token) {
      sessionStorage.setItem(
        PATIENT_AUTH_STORAGE_KEY.runtimeAccessToken,
        data.patient_access_token,
      );
      sessionStorage.setItem(
        PATIENT_AUTH_STORAGE_KEY.runtimeAccessExpiresAt,
        Date.now()
          + ((Number(data.patient_access_token_expires_in) || 3600) * 1000),
      );
    }

    if (data.patient) {
      sessionStorage.setItem(
        PATIENT_AUTH_STORAGE_KEY.patientInfo,
        JSON.stringify(data.patient),
      );
    }
    
    if (data.prefill_data) {
      sessionStorage.setItem(
        PATIENT_AUTH_STORAGE_KEY.prefillData,
        JSON.stringify(data.prefill_data),
      );
    }
  }

  /**
   * Get stored access token if valid.
   * @returns {string|null}
   */
  getAccessToken() {
    const token = sessionStorage.getItem(
      PATIENT_AUTH_STORAGE_KEY.questionnaireAccessToken,
    );
    const expires = sessionStorage.getItem(
      PATIENT_AUTH_STORAGE_KEY.questionnaireAccessExpiresAt,
    );

    if (!token || !expires) return null;

    if (Date.now() >= parseInt(expires, 10)) {
      this.clearSession();
      return null;
    }

    return token;
  }

  /**
   * Get stored session ID.
   * @returns {string|null}
   */
  getSessionId() {
    return sessionStorage.getItem(PATIENT_AUTH_STORAGE_KEY.sessionId);
  }

  /**
   * Get stored patient info.
   * @returns {Object|null}
   */
  getPatientInfo() {
    const info = sessionStorage.getItem(PATIENT_AUTH_STORAGE_KEY.patientInfo);
    return info ? JSON.parse(info) : null;
  }

  /**
   * Get stored prefill data.
   * @returns {Object|null}
   */
  getPrefillData() {
    const data = sessionStorage.getItem(PATIENT_AUTH_STORAGE_KEY.prefillData);
    return data ? JSON.parse(data) : null;
  }

  /**
   * Check if user is authenticated as existing patient.
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!this.getAccessToken();
  }

  /**
   * Clear session data (logout).
   */
  clearSession() {
    Object.values(PATIENT_AUTH_STORAGE_KEY).forEach((key) => {
      sessionStorage.removeItem(key);
    });
  }

  /**
   * Get full auth status for component initialization.
   * @returns {Object} Auth state
   */
  getAuthStatus() {
    const accessToken = this.getAccessToken();
    
    if (!accessToken) {
      return {
        authenticated: false,
        sessionId: null,
        accessToken: null,
        patient: null,
        prefillData: null,
      };
    }

    return {
      authenticated: true,
      sessionId: this.getSessionId(),
      accessToken,
      patient: this.getPatientInfo(),
      prefillData: this.getPrefillData(),
    };
  }
}

/**
 * Custom error for authentication failures.
 */
export class AuthError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

/**
 * Error thrown when duplicate visit is blocked.
 */
export class DuplicateVisitError extends Error {
  constructor(message, daysRemaining, blockedUntil, refillIntervalDays) {
    super(message);
    this.name = 'DuplicateVisitError';
    this.code = 'DUPLICATE_VISIT_BLOCKED';
    this.daysRemaining = daysRemaining;
    this.blockedUntil = blockedUntil;
    this.refillIntervalDays = refillIntervalDays;
  }
}

// Export singleton instance
const authService = new ExistingPatientAuthService();
export default authService;
